<template>
  <div>
    <div class="remove-mobile-container-padding">
      <template v-if="forceDesktop || $vuetify.display.mdAndUp">
        <EventCompetitionsDesktop
          v-bind="{
            state,
            actions,
            index,
            competitionsSortedByCode
          }"
        >
          <template v-if="!forRegistrationCompetitionsOnly" #entryLimitForm>
            <CompetitionDayLimitsForm
              v-model="limitEntriesForm"
              :event-id="event.id"
              :event-limit="eventLimit"
              :competition-day-number="index + 1"
            />
          </template>

          <template #subheading>
            <slot name="subheading" />
          </template>
          <template #competitions-column>
            <slot name="competitions-column" />
          </template>
          <template #judgments-column>
            <slot name="judgments-column" />
          </template>
          <template #prizes-column>
            <slot name="prizes-column" />
          </template>
          <template #openFor-column>
            <slot name="openFor-column" />
          </template>
          <template #registered-column>
            <slot name="registered-column" />
          </template>
          <template #fees-column>
            <slot name="fees-column" />
          </template>
        </EventCompetitionsDesktop>
      </template>
      <template v-else>
        <EventCompetitionsMobile
          v-bind="{ state, actions, index, competitionsSortedByCode }"
        >
          <template v-if="!forRegistrationCompetitionsOnly" #entryLimitForm>
            <CompetitionDayLimitsForm
              v-model="limitEntriesForm"
              :event-id="event.id"
              :event-limit="eventLimit"
              :competition-day-number="index + 1"
            />
          </template>

          <template #subheading>
            <slot name="subheading" />
          </template>
          <template #competitions-column>
            <slot name="competitions-column" />
          </template>
          <template #judgments-column>
            <slot name="judgments-column" />
          </template>
          <template #prizes-column>
            <slot name="prizes-column" />
          </template>
          <template #openFor-column>
            <slot name="openFor-column" />
          </template>
          <template #registered-column>
            <slot name="registered-column" />
          </template>
          <template #fees-column>
            <slot name="fees-column" />
          </template>
        </EventCompetitionsMobile>
      </template>
    </div>
    <br />
    <SplitCompetitionForm
      v-if="canSplit"
      ref="splitForm"
      :refetch-queries="['eventSchedule']"
    />
    <CompetitionRegistrationOverwrite
      ref="competitionRegistrationOverwrite"
      :event="event"
    />

    <EventCompetitionCloneDialog ref="competitionCloneDialog" :event="event" />
  </div>
</template>
<script>
import gql from "fraql";
import { useCurrency } from "@composables/masterdata/config";
import { capitalize, get } from "lodash-es";

import EventCompetitionsDesktop from "./EventCompetitionsDesktop.vue";
import EventCompetitionsMobile from "./EventCompetitionsMobile.vue";

import AppShowDifficultyLevel from "@AppShows/AppShowDifficultyLevel.vue";
import AppShowClassRegistrationsCount from "@AppShows/event/AppShowClassRegistrationsCount.vue";
import AppShowSections from "@AppShows/event/AppShowSections.vue";
import { COMPETITION_FEES_GQL } from "@AppShows/event/AppShowCompetitionFees.vue";
import CompetitionDayLimitsForm from "../CompetitionDayLimitsForm.vue";

import DrivingPhasesOrder from "@/utils/drivingPhasesOrder";
import { useCompetitionHelpers } from "@composables/competition_helpers";
import SplitCompetitionForm from "../SplitCompetitionForm.vue";

import AppShowCompetitionsStarts from "@/components/elements/AppShows/event/AppShowCompetitionsStarts.vue";

import AppShowCompetitionGrandPrix from "@AppShows/event/AppShowCompetitionGrandPrix.vue";
import AppShowEventcode from "@AppShows/event/AppShowEventcode.vue";

import CompetitionEntryRules from "./CompetitionEntryRules.vue";
import AppShowCompetitionCodeAndTags from "@/components/elements/AppShows/event/AppShowCompetitionCodeAndTags.vue";

import CompetitionRegistrationOverwrite from "../CompetitionRegistrationOverwrite.vue";

import EventCompetitionCloneDialog from "../EventCompetitionCloneDialog.vue";
import { showLoadingButton } from "@/utils/showLoadingButton";

import AppShowCupSeasons from "@AppShows/event/AppShowCupSeasons.vue";

export default {
  name: "EventCompetitions",
  components: {
    CompetitionDayLimitsForm,
    SplitCompetitionForm,
    EventCompetitionsDesktop,
    EventCompetitionsMobile,
    CompetitionRegistrationOverwrite,
    EventCompetitionCloneDialog
  },
  inject: {
    openClassParticipantList: {
      default: () => null
    }
  },
  props: {
    forRegistrationCompetitionsOnly: {
      type: Boolean,
      default: false
    },
    competitionDay: {
      type: String,
      default: null
    },
    event: {
      type: Object,
      required: true
    },
    index: {
      type: Number,
      required: true
    },
    forceDesktop: {
      type: Boolean,
      default: false
    }
  },
  setup() {
    const {
      competitionsOrdered,
      isIndoor,
      isRestricted,
      outsideOfCompetitionAllowed,
      isTeamCompetition
    } = useCompetitionHelpers();

    return {
      ...useCurrency(),
      competitionsOrdered,
      isIndoor,
      isRestricted,
      outsideOfCompetitionAllowed,
      isTeamCompetition
    };
  },
  data() {
    return {
      expanded: [],
      mutationLoading: false,
      form: {},
      limitEntriesForm: false
    };
  },
  computed: {
    canAdd() {
      return this.$allowedMutation(
        this.event.allowedMutations,
        "eventCompetitionCreateOrUpdate"
      );
    },
    canLimitPerDay() {
      if (this.forRegistrationCompetitionsOnly) return false;
      if (this.eventLimit) {
        return this.$allowedMutation(
          this.eventLimit.allowedMutations,
          "eventLimitUpdate"
        );
      }
      return this.$allowedMutation(
        this.event.allowedMutations,
        "eventLimitCreate"
      );
    },
    competitions() {
      if (this.forRegistrationCompetitionsOnly) {
        return this.event.competitions.filter(c => c.forRegistrationOnly);
      }
      return this.event.competitions.filter(
        c => c.startDate == this.competitionDay && !c.forRegistrationOnly
      );
    },
    eventLimit() {
      return this.event.eventLimits.find(
        x =>
          x.limitScope == "COMPETITION_DAY" &&
          x.competitionDayNumber == this.index + 1
      );
    },
    state() {
      return Object.defineProperties(
        {},
        {
          forRegistrationCompetitionsOnly: {
            get: () => this.forRegistrationCompetitionsOnly
          },
          limitEntriesForm: {
            get: () => this.limitEntriesForm
          },
          eventId: {
            get: () => this.event.id
          },
          canLimitPerDay: {
            get: () => this.canLimitPerDay
          },
          eventLimit: {
            get: () => this.eventLimit
          },
          competitionDay: {
            get: () => {
              return this.competitionDay;
            }
          },
          expanded: {
            get: () => {
              return this.expanded;
            }
          },
          canAdd: {
            get: () => {
              return this.canAdd;
            }
          },
          mutationLoading: {
            get: () => {
              return this.mutationLoading;
            }
          }
        }
      );
    },
    actions() {
      return {
        isIndoor: this.isIndoor,
        masterCurrency: this.masterCurrency,
        isRestricted: this.isRestricted,
        outsideOfCompetitionAllowed: this.outsideOfCompetitionAllowed,
        isTeamCompetition: this.isTeamCompetition,
        competitionPhases: this.competitionPhases,
        openClassParticipantList: classId => {
          this.openClassParticipantList(classId);
        },
        get,
        capitalize,
        showExpanded: this.showExpanded,
        expand: this.expand,
        arenaGround: this.arenaGround,
        anyDrivingFields: this.anyDrivingFields,
        dressageFields: this.dressageFields,
        marathonFields: this.marathonFields,
        conesFields: this.conesFields,
        splitCompetition: this.splitCompetition,
        cloneCompetition: this.cloneCompetition,
        deleteCompetition: this.deleteCompetition,
        openLimitEntriesForm: this.openLimitEntriesForm,
        openCompetitionRegistrationDateOverwriteForm:
          this.openCompetitionRegistrationDateOverwriteForm
      };
    },
    canSplit() {
      return this.competitions.some(x =>
        this.$allowedMutation(x.allowedMutations, "eventCompetitionSplit")
      );
    },
    competitionsSortedByCode() {
      return this.competitionsOrdered(this.competitions);
    }
  },
  methods: {
    showExpanded(item) {
      return (
        this.canAdd ||
        this.$allowedMutation(item.allowedMutations, "eventCompetitionSplit") ||
        this.anyDrivingFields(item)
      );
    },
    expand(item) {
      if (this.expanded.includes(item.id)) {
        this.expanded = this.expanded.filter(x => x != item.id);
      } else {
        this.expanded.push(item.id);
      }
    },
    openLimitEntriesForm() {
      this.limitEntriesForm = !this.limitEntriesForm;
    },
    openCompetitionRegistrationDateOverwriteForm(competition) {
      this.$refs.competitionRegistrationOverwrite.openForm(competition);
    },
    competitionPhases(item) {
      if (item.possiblePhases) {
        return DrivingPhasesOrder(item.possiblePhases)
          .map(x => this.$t(`events:CompetitionForm.possiblePhase.${x}.label`))
          .join(", ");
      }
    },
    arenaGround(value) {
      switch (value) {
        case "fabric":
          return this.$t(
            "events:CompetitionForm.input.driving.fabric.text",
            "Fabric"
          );
        case "grass_and_gravel":
          return this.$t(
            "events:CompetitionForm.input.driving.grassandgravel.text",
            "Grass and gravel"
          );
        case "grass":
          return this.$t(
            "events:CompetitionForm.input.driving.grass.text",
            "Grass"
          );
        case "sand":
          return this.$t(
            "events:CompetitionForm.input.driving.sand.text",
            "Sand"
          );
        case "sand_and_grass":
          return this.$t(
            "events:CompetitionForm.input.driving.sandandgrass.text",
            "Sand and grass"
          );
        case "gravel":
          return this.$t(
            "events:CompetitionForm.input.driving.gravel.text",
            "Gravel"
          );
      }
    },
    anyDrivingFields(item) {
      return (
        this.dressageFields(item) ||
        this.marathonFields(item) ||
        this.conesFields(item)
      );
    },
    dressageFields(item) {
      return item.dressageArenaSize || item.dressageArenaGround;
    },
    marathonFields(item) {
      return (
        item.marathonArenaSize ||
        item.marathonArenaGround ||
        item.marathonArenaHasWater ||
        item.marathonNumberOfObstacles
      );
    },
    conesFields(item) {
      return (
        item.conesArenaSize ||
        item.conesArenaGround ||
        item.conesClearance ||
        item.conesJudgement
      );
    },
    splitCompetition(item) {
      this.$refs.splitForm.openForm(item);
    },
    cloneCompetition(competition) {
      this.$refs.competitionCloneDialog.openForm(competition);
    },
    deleteCompetition(ID, node) {
      this.$root
        .$confirm(
          this.$t("events:schedule.Actions.deleteCompetition.heading"),
          this.$t("events:schedule.Actions.deleteCompetition.question.text")
        )
        .then(async confirm => {
          if (confirm) {
            const removeLoading = showLoadingButton(node.target);
            this.$apollo
              .mutate({
                mutation: gql`
                  mutation eventCompetitionDelete($id: ID!) {
                    eventCompetitionDelete(input: { id: $id }) {
                      event {
                        id
                        competitions {
                          id
                          name
                        }
                      }
                    }
                  }
                `,
                variables: { id: ID }
              })
              .then(() => {
                removeLoading();
                this.$root.$snackbar("success", {
                  text: this.$t(
                    "common:feedbackMessage.removedSuccessfully.text",
                    "Succesfully deleted!"
                  )
                });
              })
              .catch(error => {
                removeLoading();
                this.$root.$snackbarServerError(error, { level: "error" });
              });
          }
        });
    }
  }
};

const QUALIFICATIONRULES = gql`
  fragment _ on QualificationRule {
    id
    explanationText
    qualificationId
    qualification {
      id
      name
      qualificationGroup {
        id
        name
      }
    }
  }
`;

export const EVENT_COMPETITIONS_FRAGMENTS = {
  competitionFields: gql`
    fragment _ on Competition {
        id
        name
        eventId
        description
        code
        startDate
        registrationEndDate
        isHighlighted
        externalScheduleUrl
        forRegistrationOnly
        outsideOfCompetitionAllowed
        resultsUnlisted
        ${EventCompetitionCloneDialog.fragments.competitionFields}
        ${CompetitionEntryRules.fragments.competitionFields}
        ${AppShowCompetitionCodeAndTags.fragments.competitionFields}
        ${AppShowEventcode.fragments.competitionFields}
        ${CompetitionRegistrationOverwrite.fragments.competitionFields}
        eventcodeId
        eventcode @client {
          id
          name
        }

        competitionRuleId
        competitionRule @client {
          id
          name
        }

        competitionLevelId
        competitionLevel @client {
          id
          name
        }

        cupSeasons {
          id
          ${AppShowCupSeasons.fragments.cupSeasonFields}
          infoLink
        }
        isCupFinal

        difficultyLevelId
        difficultyLevel @client {
          ${AppShowDifficultyLevel.fragments.difficultyLevelFields}
        }
       

        publicRegistrationsDisabled

        possiblePhases
        dressageArenaSize
        dressageArenaGround
        marathonArenaSize
        marathonArenaGround
        marathonArenaHasWater
        marathonNumberOfObstacles
        conesArenaSize
        conesArenaGround
        conesClearance
        conesJudgement
        
        prizePoolCents
        prizeDistribution
        prizeText
        ${AppShowSections.fragments.competitionSections}
        ${AppShowClassRegistrationsCount.fragments.restriction}
        ${AppShowCompetitionGrandPrix.fragments.grandPrix}

        ${AppShowCompetitionsStarts.fragments.competitionFields}
        isRestricted
        isTeamCompetition
        isIndoor

        registrationsCount

        allowedFields
        allowedMutations
        
        ${COMPETITION_FEES_GQL}
    }
  `,
  qualificationRules: QUALIFICATIONRULES,
  ...CompetitionDayLimitsForm.fragments
};
</script>
